import type { RemoveMemberModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { useNavigate } from "react-router-dom";

export const RemoveMemberModal = ({
  workspaceId,
  user,
  open,
  onOpenChange,
}: RemoveMemberModalProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const { user: currentUser } = useCurrentUser();

  const logEvent = useLogAnalytics();
  const navigate = useNavigate();

  const { mutate: removeWorkspaceMembership, isPending: isLoading } =
    workspaceRouter.removeMember.useMutation({
      onError: () => {
        onOpenChange(false);
      },
      onSuccess: () => {
        void workspaceUtils.getWorkspaceAndProjectMembers.invalidate();
        void projectUtils.membership.list.invalidate();
        toast.success({
          title: "User Removed",
          description: "The user was removed successfully.",
        });
        logEvent("workspace.member.removed", {
          workspaceId,
          memberRemoved: user.email,
          removedBy: currentUser?.email,
        });

        onOpenChange(false);

        if (currentUser?.id === user.id) {
          navigate("/home");
        }
      },
    });

  const description = user.firstName
    ? `Are you sure you want to remove ${user.firstName} (${user.email}) from this workspace?`
    : "Are you sure you want to remove the user from this workspace?";

  const footer = (
    <div className="flex flex-row justify-end gap-2 w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="destructive"
        size="base"
        onClick={() => {
          return void removeWorkspaceMembership({
            userId: user.id,
            workspaceId,
          });
        }}
        loading={isLoading}
      >
        Remove
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Remove Member"
        description={description}
        footer={footer}
      />
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Remove Member"
      size="base"
      description={description}
      footer={footer}
    />
  );
};
